/**
 * Sprite / rendering nodes.
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';
export const SetSpriteKey: NodeBehaviour = {
  id: 'sprite.setKey',
  name: 'Set Sprite',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Image',
  description: 'Changes the sprite key on an entity.',
  keywords: ['image', 'texture', 'change'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('key', 'Sprite Key', 'string', ''),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSpriteKey', [ctx.resolveInput('entity'), ctx.resolveInput('key')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSpriteKey', eid, ctx.getInput<string>('key'));
  },
};

export const SetSpriteAlpha: NodeBehaviour = {
  id: 'sprite.setAlpha',
  name: 'Set Sprite Alpha',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Eye',
  description: 'Changes the alpha (opacity) of a sprite.',
  keywords: ['opacity', 'transparency', 'fade'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('alpha', 'Alpha', 'f32', 1.0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSpriteAlpha', [ctx.resolveInput('entity'), ctx.resolveInput('alpha')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSpriteAlpha', eid, ctx.getInput<number>('alpha'));
  },
};

export const SetSpriteVisible: NodeBehaviour = {
  id: 'sprite.setVisible',
  name: 'Set Visible',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Eye',
  description: 'Shows or hides an entity sprite.',
  keywords: ['show', 'hide', 'display'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('visible', 'Visible', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSpriteVisible', [ctx.resolveInput('entity'), ctx.resolveInput('visible')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSpriteVisible', eid, ctx.getInput<boolean>('visible'));
  },
};

export const SetSpriteTint: NodeBehaviour = {
  id: 'sprite.setTint',
  name: 'Set Tint',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Palette',
  description: 'Changes the tint color of a sprite.',
  keywords: ['color', 'hue'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('color', 'Color', 'color', 0xFFFFFFFF),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSpriteTint', [ctx.resolveInput('entity'), ctx.resolveInput('color')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSpriteTint', eid, ctx.getInput('color'));
  },
};

export const SetSpriteFlip: NodeBehaviour = {
  id: 'sprite.setFlip',
  name: 'Set Flip',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'FlipHorizontal',
  description: 'Flips the sprite horizontally or vertically.',
  keywords: ['mirror', 'reflect'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('flipX', 'Flip X', 'bool', false),
    input('flipY', 'Flip Y', 'bool', false),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSpriteFlip', [ctx.resolveInput('entity'), ctx.resolveInput('flipX'), ctx.resolveInput('flipY')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSpriteFlip', eid, ctx.getInput<boolean>('flipX'), ctx.getInput<boolean>('flipY'));
  },
};

// ============================================================================
// LAYER / DEPTH
// ============================================================================

export const SetSpriteLayer: NodeBehaviour = {
  id: 'sprite.setLayer',
  name: 'Set Sprite Layer',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Layers',
  description: 'Sets the render layer of a sprite.',
  keywords: ['z', 'order', 'depth'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('layer', 'Layer', 'i32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSpriteLayer', [ctx.resolveInput('entity'), ctx.resolveInput('layer')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSpriteLayer', eid, ctx.getInput<number>('layer'));
  },
};

export const SetSpriteDepth: NodeBehaviour = {
  id: 'sprite.setDepth',
  name: 'Set Sprite Depth',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Layers',
  description: 'Sets the render depth within a layer (0-1).',
  keywords: ['z', 'order', 'sorting'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('depth', 'Depth', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSpriteDepth', [ctx.resolveInput('entity'), ctx.resolveInput('depth')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSpriteDepth', eid, ctx.getInput<number>('depth'));
  },
};

export const GetSpriteLayer: NodeBehaviour = {
  id: 'sprite.getLayer',
  name: 'Get Sprite Layer',
  category: 'Sprite',
  isPure: true,
  color: '#ff6b6b',
  icon: 'Layers',
  description: 'Gets the render layer of a sprite.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('layer', 'Layer', 'i32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getSpriteLayer', [ctx.resolveInput('entity')], 'i32');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('layer', ctx.bridge.call('bridge:getSpriteLayer', eid) as number ?? 0);
  },
};

// ============================================================================
// ANIMATION
// ============================================================================

export const PlayAnimation: NodeBehaviour = {
  id: 'sprite.playAnim',
  name: 'Play Animation',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Play',
  description: 'Plays a sprite animation.',
  keywords: ['animate', 'sequence', 'frames'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('animation', 'Animation', 'string', 'idle'),
    input('loop', 'Loop', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:playAnimation', [ctx.resolveInput('entity'), ctx.resolveInput('animation'), ctx.resolveInput('loop')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:playAnimation', eid, ctx.getInput<string>('animation'), ctx.getInput<boolean>('loop'));
  },
};

export const StopAnimation: NodeBehaviour = {
  id: 'sprite.stopAnim',
  name: 'Stop Animation',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Square',
  description: 'Stops the current animation.',
  keywords: ['halt', 'pause', 'freeze'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:stopAnimation', [ctx.resolveInput('entity')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:stopAnimation', eid);
  },
};

export const PauseAnimation: NodeBehaviour = {
  id: 'sprite.pauseAnim',
  name: 'Pause Animation',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Pause',
  description: 'Pauses the current animation.',
  keywords: ['freeze', 'halt'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:pauseAnimation', [ctx.resolveInput('entity')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:pauseAnimation', eid);
  },
};

export const ResumeAnimation: NodeBehaviour = {
  id: 'sprite.resumeAnim',
  name: 'Resume Animation',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Play',
  description: 'Resumes a paused animation.',
  keywords: ['continue', 'unpause'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:resumeAnimation', [ctx.resolveInput('entity')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:resumeAnimation', eid);
  },
};

export const SetAnimationSpeed: NodeBehaviour = {
  id: 'sprite.setAnimSpeed',
  name: 'Set Animation Speed',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'FastForward',
  description: 'Sets the animation playback speed.',
  keywords: ['rate', 'fps', 'slow', 'fast'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('speed', 'Speed', 'f32', 1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setAnimationSpeed', [ctx.resolveInput('entity'), ctx.resolveInput('speed')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setAnimationSpeed', eid, ctx.getInput<number>('speed'));
  },
};

export const SetAnimationFrame: NodeBehaviour = {
  id: 'sprite.setAnimFrame',
  name: 'Set Animation Frame',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Image',
  description: 'Sets the current frame of an animation.',
  keywords: ['index', 'frame'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('frame', 'Frame', 'i32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setAnimationFrame', [ctx.resolveInput('entity'), ctx.resolveInput('frame')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setAnimationFrame', eid, ctx.getInput<number>('frame'));
  },
};

export const GetAnimationFrame: NodeBehaviour = {
  id: 'sprite.getAnimFrame',
  name: 'Get Animation Frame',
  category: 'Sprite',
  isPure: true,
  color: '#ff6b6b',
  icon: 'Image',
  description: 'Gets the current animation frame.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('frame', 'Frame', 'i32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getAnimationFrame', [ctx.resolveInput('entity')], 'i32');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('frame', ctx.bridge.call('bridge:getAnimationFrame', eid) as number ?? 0);
  },
};

export const IsAnimationPlaying: NodeBehaviour = {
  id: 'sprite.isAnimPlaying',
  name: 'Is Animation Playing',
  category: 'Sprite',
  isPure: true,
  color: '#ff6b6b',
  icon: 'Play',
  description: 'Checks if an animation is currently playing.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('playing', 'Is Playing', 'bool'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:isAnimationPlaying', [ctx.resolveInput('entity')], 'bool');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('playing', ctx.bridge.call('bridge:isAnimationPlaying', eid) as boolean ?? false);
  },
};

export const GetCurrentAnimation: NodeBehaviour = {
  id: 'sprite.getCurrentAnim',
  name: 'Get Current Animation',
  category: 'Sprite',
  isPure: true,
  color: '#ff6b6b',
  icon: 'Film',
  description: 'Gets the name of the currently playing animation.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('animation', 'Animation', 'string'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getCurrentAnimation', [ctx.resolveInput('entity')], 'string');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('animation', ctx.bridge.call('bridge:getCurrentAnimation', eid) as string ?? '');
  },
};

// ============================================================================
// SPRITE PROPERTIES
// ============================================================================

export const GetSpriteAlpha: NodeBehaviour = {
  id: 'sprite.getAlpha',
  name: 'Get Sprite Alpha',
  category: 'Sprite',
  isPure: true,
  color: '#ff6b6b',
  icon: 'Eye',
  description: 'Gets the alpha (opacity) of a sprite.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('alpha', 'Alpha', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getSpriteAlpha', [ctx.resolveInput('entity')], 'f32');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('alpha', ctx.bridge.call('bridge:getSpriteAlpha', eid) as number ?? 1);
  },
};

export const GetSpriteTint: NodeBehaviour = {
  id: 'sprite.getTint',
  name: 'Get Sprite Tint',
  category: 'Sprite',
  isPure: true,
  color: '#ff6b6b',
  icon: 'Palette',
  description: 'Gets the tint color of a sprite.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('color', 'Color', 'color'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getSpriteTint', [ctx.resolveInput('entity')], 'color');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('color', (ctx.bridge.call('bridge:getSpriteTint', eid) as number) ?? 0xFFFFFFFF);
  },
};

export const IsSpriteVisible: NodeBehaviour = {
  id: 'sprite.isVisible',
  name: 'Is Sprite Visible',
  category: 'Sprite',
  isPure: true,
  color: '#ff6b6b',
  icon: 'Eye',
  description: 'Checks if a sprite is visible.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('visible', 'Is Visible', 'bool'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:isSpriteVisible', [ctx.resolveInput('entity')], 'bool');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('visible', ctx.bridge.call('bridge:isSpriteVisible', eid) as boolean ?? true);
  },
};

export const GetSpriteSize: NodeBehaviour = {
  id: 'sprite.getSize',
  name: 'Get Sprite Size',
  category: 'Sprite',
  isPure: true,
  color: '#ff6b6b',
  icon: 'Square',
  description: 'Gets the size of a sprite in pixels.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('size', 'Size', 'vec2'),
    output('width', 'Width', 'f32'),
    output('height', 'Height', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getSpriteSize', [ctx.resolveInput('entity')], 'vec2');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const r = ctx.bridge.call('bridge:getSpriteSize', eid) as { x: number; y: number } | null;
    ctx.setOutput('size', r);
    ctx.setOutput('width', r?.x ?? 0);
    ctx.setOutput('height', r?.y ?? 0);
  },
};

export const SetSpriteOrigin: NodeBehaviour = {
  id: 'sprite.setOrigin',
  name: 'Set Sprite Origin',
  category: 'Sprite',
  color: '#ff6b6b',
  icon: 'Crosshair',
  description: 'Sets the origin/pivot point of a sprite (0-1).',
  keywords: ['pivot', 'anchor', 'center'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Origin X', 'f32', 0.5),
    input('y', 'Origin Y', 'f32', 0.5),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSpriteOrigin', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSpriteOrigin', eid, ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
};

export const SPRITE_NODES: readonly NodeBehaviour[] = [
  // Basic
  SetSpriteKey, SetSpriteAlpha, SetSpriteVisible, SetSpriteTint, SetSpriteFlip,
  // Layer/Depth
  SetSpriteLayer, SetSpriteDepth, GetSpriteLayer,
  // Animation
  PlayAnimation, StopAnimation, PauseAnimation, ResumeAnimation,
  SetAnimationSpeed, SetAnimationFrame, GetAnimationFrame,
  IsAnimationPlaying, GetCurrentAnimation,
  // Properties
  GetSpriteAlpha, GetSpriteTint, IsSpriteVisible, GetSpriteSize, SetSpriteOrigin,
];
